import styles from "./DetailExtraCard.module.css"
import { StorageItemCardProps } from "../../../types/storageItem.types";
import GalleryCard from "../../Cards/GalleryCard/GalleryCard";
import CartButtonSecondary from "../../Button/CartButton/CartButtonSecondary";
import CartAmountSecondary from "../../Button/CartButton/CartAmountSecondary";
import CloseButton from "../../Button/CloseButton/CloseButton";
import { useCartContext } from "../../../context/CartContext";
import { useDetailContext } from '../../../context/DetailContext';
import { useOrderContext } from "../../../context/OrderContext";
import useTimeModalActions from "../../../hooks/useTimeModalActions";
import { usePreferences } from "../../../context/PreferencesContext";
import { czk_eur_rate } from "../../../constants/prices";
import { czk_to_eur, formatCurrency } from "../../../utils/prices";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import { usePopup } from "../../../context/PopupContext";
import { useRef, useState } from 'react';
import { normalizeDate, denormalizeDate } from "../../../utils/calendar";
import Tooltip from "../../Tooltip/Tooltip";
import { defaultOrderTime } from "../../../constants/times";
import { getAllProductImages } from "../../../services/productImageService";
import { usePublicCatalog } from "../../../context/PublicCatalogContext";
import useCartAmountSync from "../../../hooks/useCartAmountSync";
import { getAdvanceDays, getDeliveryWindow } from "../../../utils/products";
import { getQuantityTooltip } from "../../../utils/quantity";
import ImageCarousel from "../../ImageCarousel/ImageCarousel";
interface DetailStorageItemCart extends StorageItemCardProps{
    time?: string,
    date?: string
}

/**
* Detail card for storage items
*
* @param props StorageItemCardProps
* @param {StorageItem} props.product - Product
* @returns TSX.Element
*/
const DetailExtraCard = ({ product, time, date }: DetailStorageItemCart) => {
    const { cartData, reloadCart, cartLoaded } = useCartContext();
    const { t, i18n } = useTranslation(); // Translation
    const {isPublicView} = usePublicCatalog();
    const {closeDetail} = useDetailContext(); // Detail context
    const { orderData, orderType } = useOrderContext(); // Order data
    const { openTimeModal } = useTimeModalActions(); // Time modal
    const { selectedCurrency } = usePreferences(); // Current selected currency
    const { open: openPopup, close: closePopup, setPopupTarget, setOpenOnTop: setPopupOpenOnTop } = usePopup();
    
    const effectiveDate = date ?? orderData.selectedDate; //"2025-09-08" (ISO)
    const dateKey = denormalizeDate(effectiveDate); // "8. 9. 2025"  -> matches cartData.items keys
    const dateISO = normalizeDate(effectiveDate); // "2025-09-08"  -> what cartChange expects 

    const { cartAmount, handleCart } = useCartAmountSync({
        cartData,
        cartLoaded,
        reloadCart,
        itemKey: {
            id: product.id,
            category: "storage",
            dateKey,
            dateISO,
            time,
        },
    });

    // Make sure price is always in correct currency
    const no_dph_price = product.pricing?.price_without_tax != null ? czk_to_eur(product.pricing.price_without_tax, czk_eur_rate, selectedCurrency).toFixed(2) : "N/A"
    const dph_price = product.pricing?.price_with_tax != null ? czk_to_eur(product.pricing.price_with_tax, czk_eur_rate, selectedCurrency).toFixed(2) : "N/A"
    
    // Order in advance
    const advance = getAdvanceDays(product);

    // Sample time limit
    const { min: minTime, max: maxTime } = getDeliveryWindow(product);

    const minOrder = product.min_order ?? 1;
    const granularity = product.granularity ?? 1;
    const tooltip = getQuantityTooltip({
        cartAmount,
        minOrder,
        granularity,
        language: i18n.language,
        itemType: "service",
    });
    const ref = useRef<HTMLDivElement>(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const images = getAllProductImages(product);
    return(
        <>
            <div className={styles.ProductDetail} key={product.id}>
                <div className={styles.ProductDetailBlur} onClick={closeDetail}></div>
                <div className={styles.ProductDetailCard}>
                    <div className={styles.ProductMain}>
                        {images.length > 1 ? (
                            <ImageCarousel images={images} className={styles.ProductImage} onPopup={true} />
                        ) : (
                            <GalleryCard imgSrc={images[0]} className={styles.ProductImage} onPopup={true} />
                        )}
                        <div className={styles.ProductMainText}>
                            <div className={styles.ProductMainInfo}>
                                <p className={styles.ProductTitle}>{i18n.language === "en" ? product.name_en || "N/A" : product.name}</p>
                            </div>
                            <div className={styles.ProductMainActions}>
                                <div className={styles.ProductPrices}>
                                    {product.pricing?.price_without_tax != null && (
                                        <p className={styles.NoDphPrice}>{formatCurrency(no_dph_price, selectedCurrency)}/<Trans i18nKey="products.no_dph" components={{ 1: <span/> }} /></p>
                                    )}
                                    {product.pricing?.price_with_tax != null && (
                                        <p className={styles.DphPrice}>{formatCurrency(dph_price, selectedCurrency)} {t("products.dph")}</p>
                                    )}
                                </div>
                                {(cartAmount>0 && !isPublicView) ? (
                                    <div className = {styles.Amount} ref={ref} onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} >
                                    <CartAmountSecondary amountInCart={cartAmount} 
                                        /*disabledDecrease={cartAmount <= (product?.min_order === null ? 1 : product.min_order)}*/
                                        disabledIncrease={ cartAmount >= ((product.max_order === null || product.max_order === 0) ? 100 : product.max_order)}
                                        onIncrease={() => handleCart(cartAmount+ (1*(product.granularity??1)))}
                                        onDecrease={()=>{ cartAmount > (product?.min_order ?? 1) ? handleCart(cartAmount-(1*(product.granularity??1))) :  handleCart(0)}}
                                        className={styles.CartAmount} 
                                        disabled={ isPublicView}
                                        />
                                        {showTooltip && <Tooltip desc={tooltip} className={styles.Tooltips} targetRef={ref}>{tooltip}</Tooltip>}
                                    </div>
                                ) : (
                                    <CartButtonSecondary text={t("buttons.demand")} 
                                    disabled={ isPublicView}
                                    onClick={()=>{
                                        if(orderType !== "demand"){
                                            setPopupOpenOnTop(true);
                                            setPopupTarget(
                                                {
                                                    title: t("demand_popup.title"),
                                                    content: t("demand_popup.content"),
                                                    submitBtnText: t("demand_popup.continue"),
                                                    backBtnText: t("demand_popup.back"),
                                                    onClose: (()=>{closePopup()}),
                                                    onSubmit: (() => {
                                                        openTimeModal(
                                                            {
                                                                type: "single",
                                                                date: orderData.selectedDate,
                                                                time: defaultOrderTime,
                                                                productId: product.id,
                                                                prodType: "storage",
                                                                static: true,
                                                                amount: Math.max(product.min_order ?? 1, product.granularity) ?? 1,
                                                                restrictions: { min: minTime, max: maxTime },
                                                                advance: advance,
                                                            },
                                                            { openOnTop: true }
                                                        );
                                                        closePopup();
                                                    })
                                                }
                                            )
                                            openPopup()
                                        }else{
                                            openTimeModal(
                                                {
                                                    type: "single",
                                                    date: orderData.selectedDate,
                                                    time: defaultOrderTime,
                                                    productId: product.id,
                                                    prodType: "storage",
                                                    static: true,
                                                    amount: Math.max(product.min_order ?? 1, product.granularity) ?? 1,
                                                    restrictions: { min: minTime, max: maxTime },
                                                    advance: advance,
                                                },
                                                { openOnTop: true }
                                            );
                                        }
                                    }}
                                className={styles.DetailCartButton}/>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className={styles.ProductInfo}>
                        <p className={styles.InfoTitle}>{t("products.desc")}</p>
                        <p className={styles.InfoDescription}>
                            {/*Format the description */}
                            {i18n.language === "en" ? product.description_en && (
                                product.description_en
                            ) : (
                                product.description_cz
                            )}
                        </p>
                    </div>
                    <CloseButton onClick={()=>closeDetail && closeDetail()} className={styles.Close}/>
                </div>
            </div>
        </>
    )
};

export default DetailExtraCard;
